import { useEffect } from "react";

import { useSetAtom } from "jotai";

import { Trans, useTranslation } from "@arc/i18n";
import { trimAddress, useTonWallet } from "@arc/sdk";
import { Drawer } from "@arc/ui/drawer";
import { ArrowDropdownIcon } from "@arc/ui/icons/arrow-dropdown";
import { LogoFillIcon } from "@arc/ui/icons/logo-fill";
import { ListItem } from "@arc/ui/list-item";
import { Loader } from "@arc/ui/loader";
import { Skeleton } from "@arc/ui/skeleton";

import { showMenuAtom } from "@/atoms/ui";
import { useConnectTonConnectAppMutation, useManifest } from "@/utils/tonConnect/hooks";
import { type ConnectItemReply, type ConnectRequest, type DAppManifest } from "@/utils/tonConnect/types";

import { usePincodeModal } from "./Pincode/usePincodeModal";

export const TonConnectModal = ({
  hideButton,
  params,
  isOpen,
  onAccountClick,
  onClose,
}: {
  hideButton: boolean;
  params: ConnectRequest | null;
  isOpen: boolean;
  onAccountClick: () => void;
  onClose: (replyItems?: ConnectItemReply[], manifest?: DAppManifest) => void;
}) => {
  const { t } = useTranslation("wallet");
  const setShowMenu = useSetAtom(showMenuAtom);
  const { data: manifest, isLoading, isError } = useManifest(params);

  const { promptPincode, PincodeModalComponent } = usePincodeModal();

  const { mutateAsync: handleTonConnectApp } = useConnectTonConnectAppMutation();

  const activeWallet = useTonWallet();

  useEffect(() => {
    setShowMenu(!isOpen);
  }, [isOpen, setShowMenu]);

  if (!activeWallet) {
    return null;
  }

  const handleConnectClick = async () => {
    if (!params || !manifest) return;
    try {
      const mnemonic = await promptPincode("decode");
      if (!mnemonic) {
        onClose();
        return;
      }

      const result = await handleTonConnectApp({ request: params, manifest, mnemonic });

      onClose(result, manifest);
    } catch (err) {
      console.error(err);
      onClose();
    }
  };

  return (
    <>
      <Drawer height="full" isOpen={isOpen} onClose={onClose}>
        <div className="flex h-full flex-col items-center bg-[url('/images/connect-bg.svg')] bg-contain bg-[0px_-47px] bg-no-repeat p-4">
          <div className="mt-16 flex w-full justify-around">
            <div className="border-accent bg-tonconnect-app-logo-light dark:bg-tonconnect-app-logo-dark shadow-tonconnect-app-logo-light dark:shadow-tonconnect-app-logo-dark rounded-3xl border-4">
              <LogoFillIcon className="fill-accent stroke-accent h-24 w-24 p-5" />
            </div>

            <div className="border-accent bg-tonconnect-app-logo-light dark:bg-tonconnect-app-logo-dark shadow-tonconnect-app-logo-light dark:shadow-tonconnect-app-logo-dark rounded-3xl border-4">
              {manifest?.iconUrl ? (
                <img alt={manifest?.name} className="h-24 w-24 rounded-3xl p-5" src={manifest.iconUrl} />
              ) : (
                <Skeleton className="h-24 w-24 rounded-2xl" />
              )}
            </div>
          </div>

          <div className="text-title-2 text-text mt-4 text-center">
            <Trans
              components={{
                name: manifest?.name ? (
                  <span className="text-accent">{manifest.name}</span>
                ) : (
                  <Skeleton className="-mb-1.5 mr-1 inline-block h-6 w-24" />
                ),
              }}
              i18nKey="TONCONNECT.CONNECT_TO"
              ns="wallet"
              values={{ appName: manifest?.name }}
            />
          </div>

          <div className="text-text mt-2 text-center text-base">
            <Trans
              components={{
                name: manifest?.name ? (
                  <span className="text-accent">{manifest.name}</span>
                ) : (
                  <Skeleton className="-mb-1 mr-1 inline-block h-5 w-24" />
                ),
              }}
              i18nKey="TONCONNECT.APP_REQUESTING"
              ns="wallet"
              values={{ appName: manifest?.name }}
            />
          </div>

          <div className="mt-5 w-full">
            <ListItem
              fullWidth
              leftBottomText={trimAddress(activeWallet.address)}
              leftTopText={activeWallet.walletName}
              rightIcon={<ArrowDropdownIcon className="stroke-text-secondary h-4 w-4" />}
              onClick={onAccountClick}
            />
          </div>

          <div className="text-subhead text-text-secondary mt-auto text-center">
            {isError ? (
              <span className="text-negative">{t("TONCONNECT.MANIFEST_ERROR")}</span>
            ) : (
              t("TONCONNECT.WARNING")
            )}
          </div>

          {!isError && !hideButton && (
            <button
              className="bg-accent mt-4 w-full rounded-2xl p-3 text-white disabled:opacity-50"
              disabled={isLoading}
              type="button"
              onClick={handleConnectClick}
            >
              {isLoading ? <Loader /> : t("TONCONNECT.CONNECT")}
            </button>
          )}
        </div>
      </Drawer>

      {isOpen && PincodeModalComponent}
    </>
  );
};
